import Phrase from "Models/Phrase";
import { Events } from "phaser";
import { MidiEventState, PrerenderedPhraseData } from "Types";
import { Timestamp } from "Types/ExerciseData";
import { ProgressState } from "./GameObjects/Progress";
import { PhraseData } from "./GameObjects/StaffLine";
import { COLORBLIND_TYPE } from "Utils/Constants";

export type StartPlayingArgs = {
  measureOffset: number;
  processEventsQueue: [() => void, any];
};

export enum ReactToExerciseEventType {
  UpdateObjective = "update-objective",
  PauseExercise = "pause-exercise",
  ResumeExercise = "resume-exercise",
  ToggleExercise = "toggle-exercise",
  StartExercise = "start-exercise",
  MidiOn = "midi-on",
  MidiOff = "midi-off",
  SkipExercise = "skip-exercise",
  UnloadExercise = "unload-exercise",
  AddPhrase = "add-phrase",
  PulseNote = "pulse-note",
  SetNoteState = "set-note-state",
  SetCursor = "set-cursor",
  ChangePlaybackRate = "change-playback-rate",
  ChangeTempo = "change-tempo",
  ResizeWindow = "resize-window",
  SetFreePlayMode = "set-free-play-mode",
  SetZoomLevel = "set-zoom-level",
  SoftRefreshRepertoire = "soft-refresh-repertoire",
  SetCursorVisible = "set-cursor-visible",
  ResetChevrons = "reset-chevrons",
}

type ChangePlaybackRate = [
  ReactToExerciseEventType.ChangePlaybackRate,
  { rate: number },
];
type ChangeTempo = [ReactToExerciseEventType.ChangeTempo, { tempo: number }];
type UpdateObjective = [
  ReactToExerciseEventType.UpdateObjective,
  { chapter: number; prompt?: string },
];
type PauseExercise = [ReactToExerciseEventType.PauseExercise];
type ResumeExercise = [ReactToExerciseEventType.ResumeExercise];
type ToggleExercise = [ReactToExerciseEventType.ToggleExercise];
type StartExercise = [
  ReactToExerciseEventType.StartExercise,
  { tempo?: number },
];
type UnloadExercise = [ReactToExerciseEventType.UnloadExercise];
type MidiOn = [
  ReactToExerciseEventType.MidiOn,
  { note: number; velocity: number },
];
type MidiOff = [
  ReactToExerciseEventType.MidiOff,
  { note: number; velocity: number },
];
type SkipExercise = [ReactToExerciseEventType.SkipExercise];
type PulseNote = [
  ReactToExerciseEventType.PulseNote,
  {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  },
];
type SetNoteState = [
  ReactToExerciseEventType.SetNoteState,
  {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  },
];
type SetZoomLevel = [
  ReactToExerciseEventType.SetZoomLevel,
  { zoomLevel: number },
];

// Events related to updating variables related to the lesson scene
/**
 * OSMD phrase data type for the AddPhrase event when using OSMD backend.
 * OSMDPhraseData contains musicXml string instead of prerendered SVG/JSON.
 */
export interface OSMDPhraseDataEventPayload {
  phraseRaw: Phrase;
  phrase: {
    musicXml: string;
    uuid: string;
    startTimestamp: number;
    timeSignature: { numerator: number; denominator: number };
    showClef: boolean;
    showBrace: boolean;
    showTimeSig: boolean;
  };
}

/**
 * Legacy SVG/JSON phrase data payload.
 */
interface LegacyPhraseDataEventPayload {
  phraseRaw: Phrase;
  phrase: PhraseData;
  cursorData: PrerenderedPhraseData[];
}

type AddPhrase = [
  ReactToExerciseEventType.AddPhrase,
  LegacyPhraseDataEventPayload | OSMDPhraseDataEventPayload,
];

type SetCursor = [ReactToExerciseEventType.SetCursor, { measure: number }];
type ResizeWindow = [
  ReactToExerciseEventType.ResizeWindow,
  { width: number; height: number; playButtonOverlap?: number },
];
type SetFreePlayMode = [
  ReactToExerciseEventType.SetFreePlayMode,
  { enabled: boolean },
];
type SoftRefreshRepertoire = [ReactToExerciseEventType.SoftRefreshRepertoire];
type SetCursorVisible = [
  ReactToExerciseEventType.SetCursorVisible,
  { visible: boolean },
];
type ResetChevrons = [ReactToExerciseEventType.ResetChevrons];

export type ReactToExerciseEvent =
  | UpdateObjective
  | PauseExercise
  | ResumeExercise
  | ToggleExercise
  | StartExercise
  | MidiOn
  | MidiOff
  | UnloadExercise
  | SkipExercise
  | AddPhrase
  | PulseNote
  | SetNoteState
  | SetCursor
  | ResizeWindow
  | ChangePlaybackRate
  | ChangeTempo
  | SetFreePlayMode
  | SetZoomLevel
  | SoftRefreshRepertoire
  | SetCursorVisible
  | ResetChevrons;

export enum ExerciseToReactEventType {
  ExercisePassed = "exercise-passed",
  ObjectiveComplete = "objective-complete",
  ExercisePaused = "exercise-paused",
  ExerciseResumed = "exercise-resumed",
  CurrentSceneReady = "current-scene-ready",
  ShowExercise = "show-exercise",
  ObjectiveSkipped = "objective-skipped",
  HideExercise = "hide-exercise",
  SkipButtonRequest = "skip-button-request",
  PhaserReady = "phaser-ready",
  StartedPlaying = "started-playing",
  ShowToast = "show-toast",
  HideToast = "hide-toast",
  SetPhrasesTillNextTier = "set-phrases-till-next-tier",
  SetPhraseEligible = "set-phrase-eligible",
  ShowFeedbackToast = "show-feedback-toast",
  RepertoireCompleted = "repertoire-completed",
  RepertoirePhraseCompleted = "repertoire-phrase-completed",
}

type ObjectiveComplete = [
  ExerciseToReactEventType.ObjectiveComplete,
  { jumpTo: Timestamp },
];
type ExercisePassed = [ExerciseToReactEventType.ExercisePassed];
type ExercisePaused = [
  ExerciseToReactEventType.ExercisePaused,
  { lastPlayedMeasure?: number; pausedAtTimestamp?: number },
];
type ExerciseResumed = [ExerciseToReactEventType.ExerciseResumed];
type PhaserReady = [ExerciseToReactEventType.PhaserReady];
type CurrentSceneReady = [
  ExerciseToReactEventType.CurrentSceneReady,
  Phaser.Scene,
];
type ShowExercise = [typeof ExerciseToReactEventType.ShowExercise];
type ObjectiveSkipped = [
  ExerciseToReactEventType.ObjectiveSkipped,
  { jumpTo: Timestamp },
];
type HideExercise = [ExerciseToReactEventType.HideExercise];
type SkipButtonRequest = [ExerciseToReactEventType.SkipButtonRequest];
type StartedPlaying = [
  ExerciseToReactEventType.StartedPlaying,
  StartPlayingArgs,
];
type ShowToast = [ExerciseToReactEventType.ShowToast, string];
type HideToast = [ExerciseToReactEventType.HideToast];
type SetPhrasesTillNextTier = [
  ExerciseToReactEventType.SetPhrasesTillNextTier,
  { phrasesTillNextTier: number },
];
type SetPhraseEligible = [
  ExerciseToReactEventType.SetPhraseEligible,
  { eligible: boolean },
];
type ShowFeedbackToast = [
  ExerciseToReactEventType.ShowFeedbackToast,
  {
    state: MidiEventState;
  },
];
type RepertoireCompleted = [ExerciseToReactEventType.RepertoireCompleted];
type RepertoirePhraseCompleted = [
  ExerciseToReactEventType.RepertoirePhraseCompleted,
  { phraseIndex: number },
];

type ExerciseToReactEvent =
  | ExercisePassed
  | ObjectiveComplete
  | ExercisePaused
  | ExerciseResumed
  | CurrentSceneReady
  | HideExercise
  | ObjectiveSkipped
  | ShowExercise
  | PhaserReady
  | StartedPlaying
  | SkipButtonRequest
  | ShowToast
  | HideToast
  | SetPhraseEligible
  | SetPhrasesTillNextTier
  | ShowFeedbackToast
  | RepertoireCompleted
  | RepertoirePhraseCompleted;

export const UpdateObjective = (chapter: number, prompt?: string) =>
  [
    ReactToExerciseEventType.UpdateObjective,
    { prompt, chapter },
  ] as UpdateObjective;
export const UnloadExercise = () =>
  [ReactToExerciseEventType.UnloadExercise] as UnloadExercise;
export const PauseExercise = () =>
  [ReactToExerciseEventType.PauseExercise] as PauseExercise;
export const ResumeExercise = () =>
  [ReactToExerciseEventType.ResumeExercise] as ResumeExercise;
export const StartExercise = (tempo?: number) =>
  [ReactToExerciseEventType.StartExercise, { tempo }] as StartExercise;
export const MidiOn = (note: number, velocity: number) =>
  [ReactToExerciseEventType.MidiOn, { note, velocity }] as MidiOn;
export const MidiOff = (note: number) =>
  [ReactToExerciseEventType.MidiOff, { note }] as MidiOff;
export const SkipExercise = () =>
  [ReactToExerciseEventType.SkipExercise] as SkipExercise;
export const ToggleExercise = () =>
  [ReactToExerciseEventType.ToggleExercise] as ToggleExercise;
export const SetFreePlayMode = (enabled: boolean) =>
  [ReactToExerciseEventType.SetFreePlayMode, { enabled }] as SetFreePlayMode;
export const SoftRefreshRepertoire = () =>
  [ReactToExerciseEventType.SoftRefreshRepertoire] as SoftRefreshRepertoire;
export const SetCursorVisible = (visible: boolean) =>
  [ReactToExerciseEventType.SetCursorVisible, { visible }] as SetCursorVisible;
export const ResetChevrons = () =>
  [ReactToExerciseEventType.ResetChevrons] as ResetChevrons;
export const AddPhrase = (
  phraseRaw: Phrase,
  phrase: PhraseData,
  cursorData: PrerenderedPhraseData[],
) =>
  [
    ReactToExerciseEventType.AddPhrase,
    { phraseRaw, phrase, cursorData },
  ] as AddPhrase;
/**
 * Create an AddPhrase event with OSMD-compatible payload (MusicXML instead of SVG/JSON).
 * Used by MainAppPhaser when starting OSMDSightReadingScene.
 */
export const AddOSMDPhrase = (
  phraseRaw: Phrase,
  showClef: boolean = true,
  showBrace: boolean = true,
  showTimeSig: boolean = true,
): AddPhrase => {
  const payload: OSMDPhraseDataEventPayload = {
    phraseRaw,
    phrase: {
      musicXml: phraseRaw.musicXML.xml,
      uuid: phraseRaw.uuid,
      startTimestamp: phraseRaw.getStartTimestamp(),
      timeSignature: {
        numerator: phraseRaw.musicXML.timeSignatures[0]?.numerator ?? 4,
        denominator: phraseRaw.musicXML.timeSignatures[0]?.denominator ?? 4,
      },
      showClef,
      showBrace,
      showTimeSig,
    },
  };
  return [ReactToExerciseEventType.AddPhrase, payload] as AddPhrase;
};
export const SetCursor = (measure: number) =>
  [ReactToExerciseEventType.SetCursor, { measure }] as SetCursor;
export const SetNoteState = (
  id: string,
  state: ProgressState,
  colorblindType?: COLORBLIND_TYPE | null,
  noteTimestamp?: number,
) =>
  [
    ReactToExerciseEventType.SetNoteState,
    { id, state, colorblindType, noteTimestamp },
  ] as SetNoteState;
export const PulseNote = (
  id: string,
  colorblindType?: COLORBLIND_TYPE | null,
  noteTimestamp?: number,
) =>
  [
    ReactToExerciseEventType.PulseNote,
    { id, colorblindType, noteTimestamp },
  ] as PulseNote;
export const ChangePlaybackRate = (rate: number) =>
  [ReactToExerciseEventType.ChangePlaybackRate, { rate }] as ChangePlaybackRate;
export const ChangeTempo = (tempo: number) =>
  [ReactToExerciseEventType.ChangeTempo, { tempo }] as ChangeTempo;
export const ResizeWindow = (
  width: number,
  height: number,
  playButtonOverlap?: number,
) =>
  [
    ReactToExerciseEventType.ResizeWindow,
    { width, height, playButtonOverlap },
  ] as ResizeWindow;
export const SetZoomLevel = (zoomLevel: number) =>
  [ReactToExerciseEventType.SetZoomLevel, { zoomLevel }] as SetZoomLevel;

export const ListenUnloadExercise = (cb: () => void) =>
  [ReactToExerciseEventType.UnloadExercise, cb] as Cb<UnloadExercise>;
export const ListenUpdateObjective = (
  cb: ({ chapter }: { chapter: number }) => void,
) => [ReactToExerciseEventType.UpdateObjective, cb] as Cb<UpdateObjective>;
export const ListenPauseExercise = (cb: () => void) =>
  [ReactToExerciseEventType.PauseExercise, cb] as Cb<PauseExercise>;
export const ListenResumeExercise = (cb: () => void) =>
  [ReactToExerciseEventType.ResumeExercise, cb] as Cb<ResumeExercise>;
export const ListenToggleExercise = (cb: () => void) =>
  [ReactToExerciseEventType.ToggleExercise, cb] as Cb<ToggleExercise>;
export const ListenStartExercise = (
  cb: ({ tempo }: { tempo?: number }) => void,
) => [ReactToExerciseEventType.StartExercise, cb] as Cb<StartExercise>;
export const ListenMidiOn = (
  cb: (arg: { note: number; velocity: number }) => void,
) => [ReactToExerciseEventType.MidiOn, cb] as Cb<MidiOn>;
export const ListenMidiOff = (cb: (arg: { note: number }) => void) =>
  [ReactToExerciseEventType.MidiOff, cb] as Cb<MidiOff>;
export const ListenSkipExercise = (cb: () => void) =>
  [ReactToExerciseEventType.SkipExercise, cb] as Cb<SkipExercise>;
export const ListenAddPhrase = (
  cb: (arg: {
    phraseRaw: Phrase;
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
  }) => void,
) => [ReactToExerciseEventType.AddPhrase, cb] as Cb<AddPhrase>;

/**
 * Listener for OSMD phrase data.
 * Uses the same AddPhrase event but with OSMD-specific payload structure.
 * The callback types are compatible at runtime since they share the same event type.
 */
export const ListenAddOSMDPhrase = (
  cb: (arg: OSMDPhraseDataEventPayload) => void,
): Cb<[ReactToExerciseEventType.AddPhrase, OSMDPhraseDataEventPayload]> => [
  ReactToExerciseEventType.AddPhrase,
  cb,
];

export const ListenSetCursor = (cb: (arg: { measure: number }) => void) =>
  [ReactToExerciseEventType.SetCursor, cb] as Cb<SetCursor>;
export const ListenPulseNote = (
  cb: (arg: {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) => void,
) => [ReactToExerciseEventType.PulseNote, cb] as Cb<PulseNote>;
export const ListenSetNoteState = (
  cb: (arg: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) => void,
) => [ReactToExerciseEventType.SetNoteState, cb] as Cb<SetNoteState>;
export const ListenChangePlaybackRate = (cb: (arg: { rate: number }) => void) =>
  [ReactToExerciseEventType.ChangePlaybackRate, cb] as Cb<ChangePlaybackRate>;
export const ListenChangeTempo = (cb: (arg: { tempo: number }) => void) =>
  [ReactToExerciseEventType.ChangeTempo, cb] as Cb<ChangeTempo>;
export const ListenResizeWindow = (
  cb: (arg: {
    width: number;
    height: number;
    playButtonOverlap?: number;
  }) => void,
) => [ReactToExerciseEventType.ResizeWindow, cb] as Cb<ResizeWindow>;
export const ListenSetFreePlayMode = (
  cb: (arg: { enabled: boolean }) => void,
) => [ReactToExerciseEventType.SetFreePlayMode, cb] as Cb<SetFreePlayMode>;
export const ListenSetZoomLevel = (cb: (arg: { zoomLevel: number }) => void) =>
  [ReactToExerciseEventType.SetZoomLevel, cb] as Cb<SetZoomLevel>;
export const ListenSoftRefreshRepertoire = (cb: () => void) =>
  [
    ReactToExerciseEventType.SoftRefreshRepertoire,
    cb,
  ] as Cb<SoftRefreshRepertoire>;
export const ListenSetCursorVisible = (
  cb: (arg: { visible: boolean }) => void,
) => [ReactToExerciseEventType.SetCursorVisible, cb] as Cb<SetCursorVisible>;
export const ListenResetChevrons = (cb: () => void) =>
  [ReactToExerciseEventType.ResetChevrons, cb] as Cb<ResetChevrons>;

export const ExercisePassed = () =>
  [ExerciseToReactEventType.ExercisePassed] as ExercisePassed;
export const ObjectiveComplete = (jumpTo: Timestamp) =>
  [ExerciseToReactEventType.ObjectiveComplete, { jumpTo }] as ObjectiveComplete;
export const ExercisePaused = (
  lastPlayedMeasure?: number,
  pausedAtTimestamp?: number,
) =>
  [
    ExerciseToReactEventType.ExercisePaused,
    { lastPlayedMeasure, pausedAtTimestamp },
  ] as ExercisePaused;
export const ExerciseResumed = () =>
  [ExerciseToReactEventType.ExerciseResumed] as ExerciseResumed;
export const ObjectiveSkipped = (jumpTo: Timestamp) =>
  [ExerciseToReactEventType.ObjectiveSkipped, { jumpTo }] as ObjectiveSkipped;
export const HideExercise = () =>
  [ExerciseToReactEventType.HideExercise] as HideExercise;
export const ShowExercise = () =>
  [ExerciseToReactEventType.ShowExercise] as ShowExercise;
export const SkipButtonRequest = () =>
  [ExerciseToReactEventType.SkipButtonRequest] as SkipButtonRequest;
export const CurrentSceneReady = (scene: Phaser.Scene) =>
  [ExerciseToReactEventType.CurrentSceneReady, scene] as CurrentSceneReady;
export const PhaserReady = () =>
  [ExerciseToReactEventType.PhaserReady] as PhaserReady;
export const StartedPlaying = (args?: StartPlayingArgs) =>
  [ExerciseToReactEventType.StartedPlaying, args] as StartedPlaying;
export const ShowToast = (message: string) =>
  [ExerciseToReactEventType.ShowToast, message] as ShowToast;
export const HideToast = () =>
  [ExerciseToReactEventType.HideToast] as HideToast;
export const SetPhrasesTillNextTier = (phrasesTillNextTier: number) =>
  [
    ExerciseToReactEventType.SetPhrasesTillNextTier,
    { phrasesTillNextTier },
  ] as SetPhrasesTillNextTier;
export const SetPhraseEligible = (eligible: boolean) =>
  [
    ExerciseToReactEventType.SetPhraseEligible,
    { eligible },
  ] as SetPhraseEligible;
export const ShowFeedbackToast = (state: MidiEventState) =>
  [ExerciseToReactEventType.ShowFeedbackToast, { state }] as ShowFeedbackToast;
export const RepertoireCompleted = () =>
  [ExerciseToReactEventType.RepertoireCompleted] as RepertoireCompleted;
export const RepertoirePhraseCompleted = (phraseIndex: number) =>
  [
    ExerciseToReactEventType.RepertoirePhraseCompleted,
    { phraseIndex },
  ] as RepertoirePhraseCompleted;
export const ListenExercisePassed = (cb: () => void) =>
  [ExerciseToReactEventType.ExercisePassed, cb] as Cb<ExercisePassed>;
export const ListenObjectiveComplete = (
  cb: (arg: { jumpTo: Timestamp }) => void,
) => [ExerciseToReactEventType.ObjectiveComplete, cb] as Cb<ObjectiveComplete>;
export const ListenExercisePaused = (
  cb: ({
    lastPlayedMeasure,
    pausedAtTimestamp,
  }: {
    lastPlayedMeasure?: number;
    pausedAtTimestamp?: number;
  }) => void,
) => [ExerciseToReactEventType.ExercisePaused, cb] as Cb<ExercisePaused>;
export const ListenExerciseResumed = (cb: () => void) =>
  [ExerciseToReactEventType.ExerciseResumed, cb] as Cb<ExerciseResumed>;
export const ListenObjectiveSkipped = (
  cb: (arg: { jumpTo: Timestamp }) => void,
) => [ExerciseToReactEventType.ObjectiveSkipped, cb] as Cb<ObjectiveSkipped>;
export const ListenHideExercise = (cb: () => void) =>
  [ExerciseToReactEventType.HideExercise, cb] as Cb<HideExercise>;
export const ListenShowExercise = (cb: () => void) =>
  [ExerciseToReactEventType.ShowExercise, cb] as Cb<ShowExercise>;
export const ListenSkipButtonRequest = (cb: () => void) =>
  [ExerciseToReactEventType.SkipButtonRequest, cb] as Cb<SkipButtonRequest>;
export const ListenCurrentSceneReady = (cb: () => void) =>
  [ExerciseToReactEventType.CurrentSceneReady, cb] as Cb<CurrentSceneReady>;
export const ListenPhaserReady = (cb: () => void) =>
  [ExerciseToReactEventType.PhaserReady, cb] as Cb<PhaserReady>;
export const ListenStartedPlaying = (cb: (args?: StartPlayingArgs) => void) =>
  [ExerciseToReactEventType.StartedPlaying, cb] as Cb<StartedPlaying>;
export const ListenShowToast = (cb: (message: string) => void) =>
  [ExerciseToReactEventType.ShowToast, cb] as Cb<ShowToast>;
export const ListenHideToast = (cb: () => void) =>
  [ExerciseToReactEventType.HideToast, cb] as Cb<HideToast>;
export const ListenSetPhrasesTillNextTier = (
  cb: (arg: { phrasesTillNextTier: number }) => void,
) =>
  [
    ExerciseToReactEventType.SetPhrasesTillNextTier,
    cb,
  ] as Cb<SetPhrasesTillNextTier>;
export const ListenSetPhraseEligible = (
  cb: (arg: { eligible: boolean }) => void,
) => [ExerciseToReactEventType.SetPhraseEligible, cb] as Cb<SetPhraseEligible>;
export const ListenShowFeedbackToast = (
  cb: (arg: { state: MidiEventState }) => void,
) => [ExerciseToReactEventType.ShowFeedbackToast, cb] as Cb<ShowFeedbackToast>;
export const ListenRepertoireCompleted = (cb: () => void) =>
  [ExerciseToReactEventType.RepertoireCompleted, cb] as Cb<RepertoireCompleted>;
export const ListenRepertoirePhraseCompleted = (
  cb: (arg: { phraseIndex: number }) => void,
) =>
  [
    ExerciseToReactEventType.RepertoirePhraseCompleted,
    cb,
  ] as Cb<RepertoirePhraseCompleted>;
// Used to emit events between React components and Phaser scenes
// https://newdocs.phaser.io/docs/3.70.0/Phaser.Events.EventEmitter
export const EventBus = new Events.EventEmitter();

// Queue of events that dont currently have any listeners registered for them
const waitingQueueFromReact = new Map<
  ReactToExerciseEventType,
  ReactToExerciseEvent[]
>();
const waitingQueueFromPhaser = new Map<
  ExerciseToReactEventType,
  ExerciseToReactEvent[]
>();

/// Send event from phaser to react
export const NotifyReact = ([type, payload]: ExerciseToReactEvent) => {
  console.debug("notifying react: ", type, payload);
  EventBus.emit(type, payload);
  if (EventBus.listeners(type).length === 0) {
    // There are no listeners registered for this event yet. Push this to the back of the queue in case
    // someone comes looking for it later
    const queue = waitingQueueFromPhaser.get(type);
    if (!queue) {
      waitingQueueFromPhaser.set(type, [
        [type, payload] as ExerciseToReactEvent,
      ]);
    } else {
      queue.push([type, payload] as ExerciseToReactEvent);
    }
  }
};
/// Send event from react to phaser
export const NotifyPhaser = ([type, payload]: ReactToExerciseEvent) => {
  console.debug("notifying phaser", type, payload);
  if (EventBus.listeners(type).length === 0) {
    // There are no listeners registered for this event yet. Push this to the back of the queue in case
    // someone comes looking for it later
    const queue = waitingQueueFromReact.get(type);
    if (!queue) {
      waitingQueueFromReact.set(type, [
        [type, payload] as ReactToExerciseEvent,
      ]);
    } else {
      queue.push([type, payload] as ReactToExerciseEvent);
    }
  }
  EventBus.emit(type, payload);
};

export type Cb<T extends [any, any?]> = [[T][0][0], (arg: T[1]) => void];

/// Listen for events emitted by react
export function ListenForReactEvent<T extends ReactToExerciseEvent>(
  cb: Cb<T>,
  ctx?: object,
  once?: boolean,
  collectWaiting?: boolean,
) {
  if (collectWaiting) {
    const [type] = cb;
    const queue = waitingQueueFromReact.get(type);
    console.log("collecting");
    if (queue) {
      let popped = queue.shift();
      while (popped) {
        const [_, payload] = popped;
        cb[1].call(ctx, payload);
        if (once) return;
        popped = queue.shift();
      }
    }
  }
  once ? EventBus.once(cb[0], cb[1], ctx) : EventBus.on(cb[0], cb[1], ctx);
}

/// Listen for events emitted by phaser
export function ListenForPhaserEvent<T extends ExerciseToReactEvent>(
  cb: Cb<T>,
  ctx?: object,
  collectWaiting?: boolean,
) {
  if (collectWaiting) {
    const [type] = cb;
    const queue = waitingQueueFromPhaser.get(type);
    if (queue) {
      let popped = queue.shift();
      while (popped) {
        const [_, payload] = popped;
        cb[1].call(ctx, payload);
        popped = queue.shift();
      }
    }
  }
  EventBus.on(cb[0], cb[1], ctx);
}

export function ClearQueue(type: ReactToExerciseEventType) {
  waitingQueueFromReact.get(type)?.clear();
}

export const RemoveListener = (
  event: ReactToExerciseEventType | ExerciseToReactEventType,
  fn?: () => void,
) => EventBus.off(event, fn);
